// src/components/TwoFASettings.tsx
import React, { useState } from 'react';
import '../styles/TwoFASettings.css';
import Button from './Button';
import Modal from './Modal';
import LoadingIndicator from './LoadingIndicator';
import {
  getEnable2FA,
  enable2FA,
  disable2FA,
  regenerateBackupCodes,
  request2FARecovery,
} from '../services/twoFactorAuthService';
import { getCurrentUser } from '../services/userService';
import { useTwoFAStatus } from '../hooks/useTwoFAStatus';
import { NotificationInfo } from '../types/NotificationInfo';
import { QRCodeSVG } from 'qrcode.react';

type Stage =
  | 'view'
  | 'enable'
  | 'initBackup'
  | 'regenBackup'
  | 'disable'
  | 'recovery';

export const TwoFASettings: React.FC<{
  setNotification: (n: NotificationInfo) => void;
}> = ({ setNotification }) => {
  const [qrUri, setQrUri] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [stage, setStage] = useState<Stage>('view');
  const [loading, setLoading] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const {
    data: twoFA,
    isLoading: isStatusLoading,
    isError: isStatusError,
    refetch: refetchTwoFA,
  } = useTwoFAStatus();


  // **読み込み中・エラー時のハンドリング**
  if (isStatusLoading) {
    return <LoadingIndicator size={40} isFullPage />;
  }
  if (isStatusError || !twoFA) {
    return <div>2FA状態の取得に失敗しました。</div>;
  }

  const { is2faEnabled, isLineUser } = twoFA;

  // LINEユーザーはそもそも設定画面なし
  if (isLineUser) {
    return (
      <div className="line-twofa-section">
        LINEログインユーザーは2段階認証を設定できません
      </div>
    );
  }

  // 2FA 有効化フロー開始
  const handleEnable = async () => {
    setLoading(true);
    try {
      const res = await getEnable2FA();
      setQrUri(res.data.provisioning_uri);
      setStage('enable');
    } catch {
      setNotification({
        message: 'QR取得に失敗しました',
        bgColor: '#dc3545',
        textColor: '#fff',
      });
    } finally {
      setLoading(false);
    }
  };

  // 新規有効化確定 → バックアップ取得
  const handleConfirmEnable = async () => {
    setLoading(true);
    try {
      // 1) 有効化
      await enable2FA(verificationCode);
      // 2) フラグ更新
      await refetchTwoFA();
      // 3) バックアップコードを取得
      const res = await regenerateBackupCodes();
      setBackupCodes(res.data.backup_codes);
      // 4) モーダルを「初回表示」に
      setStage('initBackup');
      setNotification({
        message: '2FAを有効化しました',
        bgColor: '#28a745',
        textColor: '#fff',
      });
    } catch {
      setNotification({
        message: '有効化に失敗しました',
        bgColor: '#dc3545',
        textColor: '#fff',
      });
    } finally {
      setLoading(false);
    }
  };

  // バックアップコード生成
  const handleModalRegenerate = async () => {
    setRegenLoading(true);
    try {
      const res = await regenerateBackupCodes();
      setBackupCodes(res.data.backup_codes);
    } catch {
      setNotification({
        message: 'バックアップコード再生成に失敗しました',
        bgColor: '#dc3545',
        textColor: '#fff',
      });
    } finally {
      setRegenLoading(false);
    }
  };

  // 2FAリセットメール送信
  const sendRecoveryEmail = async () => {
    setLoading(true);
    try {
      const me = await getCurrentUser();
      await request2FARecovery(me.data.email);
      setNotification({
        message: 'リセットリンクを送信しました',
        bgColor: '#28a745',
        textColor: '#fff',
      });
      await refetchTwoFA()
      setStage('view');
    } catch {
      setNotification({
        message: 'メール送信に失敗しました',
        bgColor: '#dc3545',
        textColor: '#fff',
      });
    } finally {
      setLoading(false);
    }
  };

  // 2FA 無効化
  const handleConfirmDisable = async () => {
    setLoading(true);
    try {
      await disable2FA(disableCode);
      setNotification({
        message: '2FAを無効化しました',
        bgColor: '#28a745',
        textColor: '#fff',
      });
      await refetchTwoFA()
      setStage('view');
    } catch {
      setNotification({
        message: '無効化に失敗しました：コードをご確認ください',
        bgColor: '#dc3545',
        textColor: '#fff',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="twofa-section">
      <h2 className="section-title">2段階認証設定</h2>
      <div className="twofa-field">
        状態：<span className="twofa-state">{is2faEnabled ? '有効' : '無効'}</span>
      </div>

      {stage === 'view' && (
        <>
          {is2faEnabled ? (
            <div className="twofa-settings-field">
              <Button
                onClick={() => setStage('recovery')}
                disabled={loading}
                width="260px"
              >
                {loading ? <LoadingIndicator size={20} /> : '2FAをリセットする'}
              </Button>
              <Button
                onClick={() => setStage('disable')}
                disabled={loading}
                width="260px"
              >
                {loading ? <LoadingIndicator size={20} /> : '2FA無効化'}
              </Button>
              <Button
                onClick={() => {
                  setBackupCodes([]);
                  setStage('regenBackup');
                }}
                disabled={loading}
                width="260px"
              >
                バックアップコード再生成
              </Button>
            </div>
          ) : (
            <Button onClick={handleEnable} disabled={loading} width="260px">
              {loading ? <LoadingIndicator size={20} /> : '2FA有効化'}
            </Button>
          )}
        </>
      )}

      {stage === 'enable' && (
        <Modal isOpen onClose={() => setStage('view')}>
          {loading ? (
            <LoadingIndicator size={50} />
          ) : (
            <>
              <h2>2FA有効化</h2>
              <p>2段階認証アプリでQRコードを読み取って、取得した認証コードを入力してください。</p>
              <div className="qr-wrapper">
                <QRCodeSVG value={qrUri} size={200} level="M" />
              </div>
              <input
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value)}
                placeholder="認証コードを入力"
                autoFocus
              />
              <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 16, marginTop: '1rem' }}>
                <Button onClick={handleConfirmEnable} disabled={!verificationCode}>
                  有効化
                </Button>
              </div>
            </>
          )}
        </Modal>
      )}

      {stage === 'initBackup' && (
        <Modal isOpen onClose={() => setStage('view')}>
          {regenLoading ? (
            <LoadingIndicator size={50} />
          ) : (
            <>
              <h2>バックアップコード</h2>
              <p>2段階認証アプリを紛失した際に必要です。</p>
              <p>このコードは一度閉じると、2度と表示されません。</p>
              <ul className="backup-list">
                {backupCodes.map(c => (
                  <li key={c}>
                    <code>{c}</code>
                  </li>
                ))}
              </ul>
              <Button onClick={() => setStage('view')}>閉じる</Button>
            </>
          )}
        </Modal>
      )}

      {stage === 'regenBackup' && (
        <Modal isOpen onClose={() => setStage('view')}>
          {regenLoading ? (
            <LoadingIndicator size={50} />
          ) : (
            <>
              <h2>バックアップコード再生成</h2>
              <ul className="backup-list">
                {backupCodes.map(c => (
                  <li key={c}>
                    <code>{c}</code>
                  </li>
                ))}
              </ul>
              <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 16, marginTop: '1rem' }}>
                <Button onClick={handleModalRegenerate} disabled={regenLoading}>
                  再生成
                </Button>
                <Button onClick={() => setStage('view')}>閉じる</Button>
              </div>
            </>
          )}
        </Modal>
      )}

      {stage === 'recovery' && (
        <Modal isOpen onClose={() => setStage('view')}>
          {loading ? (
            <LoadingIndicator size={50} />
          ) : (
            <>
              <h2>2FAリセット</h2>
              <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 16, marginTop: '1rem' }}>
                <Button onClick={sendRecoveryEmail} disabled={loading}>
                  リセットリンク送信
                </Button>
              </div>
            </>
          )}
        </Modal>
      )}

      {stage === 'disable' && (
        <Modal isOpen onClose={() => setStage('view')}>
          {loading ? (
            <LoadingIndicator size={50} />
          ) : (
            <>
              <h2>2FA無効化の確認</h2>
              <input
                value={disableCode}
                onChange={e => setDisableCode(e.target.value)}
                placeholder="認証コードを入力"
                autoFocus
              />
              <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 16, marginTop: '1rem' }}>
                <Button onClick={handleConfirmDisable} disabled={!disableCode}>
                  無効化する
                </Button>
                <Button onClick={() => setStage('view')}>キャンセル</Button>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
};

export default TwoFASettings;
